import _ from 'lodash';

import Entity from 'lib/Entity';
import OfficeHours from 'entities/OfficeHours';
import PublicDevice from 'entities/PublicDevice';
import Subscriptions from 'collections/Subscriptions';
import Device from 'entities/Device';
import Group from 'entities/Group';
import Subscription from 'entities/Subscription';
import { DenialReason } from 'phoneCalls/components/PhoneCallModal';

// The user in a GroupMembership (Group <-> User relationship)
// This is the same as User, and has the same id, but only a subset of the properties of a User
class Subscriber extends Entity {
  // @ts-expect-error ts-migrate(2425) FIXME: Class 'Entity' defines instance member property 'u... Remove this comment to see the full error message
  urlRoot() {
    return '/subscribers';
  }

  parse(response: any) {
    const subscriber = _.clone(response);
    if (subscriber['devices']) {
      subscriber['devices'] = subscriber['devices'].map((device: Device) => {
        return PublicDevice.fromJSON(device);
      });
    }
    // somehow this gets omitted sometimes.
    // https://rollbar.com/Remind/r101-frontend/items/13949/
    // treat omission as an empty collection
    subscriber['subscriptions'] = Subscriptions.fromJSON(subscriber['subscriptions']);

    if (subscriber['subscribed_at']) {
      subscriber['subscribed_at'] = new Date(subscriber['subscribed_at']);
    }
    if (subscriber['office_hours']) {
      subscriber['office_hours'] = OfficeHours.fromJSON(subscriber['office_hours']);
    }
    return subscriber;
  }

  getName() {
    return this.get('name');
  }

  getNote() {
    return this.get('note');
  }

  getRole() {
    return this.get('role');
  }

  getFirstName(): string {
    return this.get('first_name') || '';
  }

  getLastName(): string {
    return this.get('last_name') || '';
  }

  getFullName(): string {
    return `${this.getFirstName()} ${this.getLastName()}`;
  }

  getSignature() {
    return this.get('signature');
  }

  getOfficeHours(): OfficeHours | undefined {
    return this.get('office_hours');
  }

  getSubscribedAt(): Date | undefined {
    return this.get('subscribed_at');
  }

  getMediums() {
    return this.get('mediums');
  }

  getDevices(): PublicDevice[] | undefined {
    return this.get('devices');
  }

  isNotable() {
    return this.get('notable');
  }

  getContactabilityStatus() {
    return this.get('contactability_status');
  }

  isBlocked() {
    return this.get('blocked');
  }

  getUntrustedName() {
    return this.get('untrusted_name');
  }

  getColor() {
    return this.get('color');
  }

  getInitials() {
    return this.get('initials');
  }

  getProfilePictureUrl(): string | undefined {
    return this.get('profile_picture_url');
  }

  getUuid() {
    return this.get('uuid');
  }

  getCanPhone() {
    return this.get('can_phone');
  }

  getCanPhoneDenialReason(): DenialReason {
    return this.get('can_phone_denial_reason');
  }

  getPreferredLanguage(): string {
    const prefs = this.get('preferences');
    if (prefs) {
      return prefs['preferred_language'];
    } else {
      return 'none';
    }
  }

  setNote(note: string) {
    this.set({ note });
  }

  setFirstName(firstName: string) {
    this.set({ first_name: firstName });
  }

  setLastName(lastName: string) {
    this.set({ last_name: lastName });
  }

  getSubscriptions(): Subscriptions {
    return this.get('subscriptions');
  }

  getCanBeCopiedToClass() {
    return this.get('can_be_copied_to_class');
  }

  subscribe(subscription: Subscription) {
    this.get('subscriptions').add(subscription);
  }

  unsubscribeFromGroup(group: Group) {
    const subscriptions = this.getSubscriptions();
    subscriptions
      .filter((s) => s.getGroupId() === group.id)
      .forEach((sub) => {
        subscriptions.remove(sub.id);
      });
  }

  confirmUntrustedName() {
    this.set({ untrusted_name: false });
  }

  toJSON() {
    const json = super.toJSON();
    if (json['subscriptions']) {
      json['subscriptions'] = this.get('subscriptions').toJSON();
    }
    return json;
  }
}

export default Subscriber;
